import { featureFlags } from '@seen/core/featureFlags'
import * as appointments from '@seen/core/models/appointments/index'
import { Participant } from '@seen/core/models/patients/participant'
import { CarePlanSignatureTask } from '@seen/core/models/tasks/signature/carePlanSignatureTask'
import { SDRSignatureTask } from '@seen/core/models/tasks/signature/sdrSignatureTask'
import { DocumentReviewTask } from '@seen/core/models/tasks/standard/documentReviewTask'
import { MedicationRefillTask } from '@seen/core/models/tasks/standard/medicationRefillTask'
import { QueuedEditsReviewTask } from '@seen/core/models/tasks/standard/queuedEditsReviewTask'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { isOfType, parseReferenceString } from '@seen/core/utils'
import { Button, cn, Separator } from '@seen/ui'
import { IconChevronRight, IconLink } from '@tabler/icons-react'
import { compact } from 'lodash-es'
import { type ReactNode, useMemo } from 'react'
import { toast } from 'sonner'
import { Activity } from '#src/components/activity/Activity.tsx'
import { AppointmentBadge } from '#src/components/AppointmentBadge.tsx'
import { ResourceFieldForm } from '#src/components/core/form/ResourceFieldForm.tsx'
import { Link } from '#src/components/core/Link.tsx'
import { ScrollAreaContent } from '#src/components/core/scroll-area/ScrollAreaContent.tsx'
import { ScrollAreaHeader } from '#src/components/core/scroll-area/ScrollAreaHeader.tsx'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { IconTaskState } from '#src/components/icons/IconTaskState.tsx'
import { DatePicker } from '#src/components/input/DatePicker.tsx'
import { OrderMention } from '#src/components/OrderMention.tsx'
import { OwnerComboboxButton } from '#src/components/OwnerComboboxButton.tsx'
import { ParticipantComboboxButton } from '#src/components/ParticipantComboboxButton.tsx'
import { QueuedEditsReviewTaskCard } from '#src/components/queued-edits/QueuedEditsReviewTaskCard.tsx'
import { RichTextEditor } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { TaskPriorityCombobox } from '#src/components/task/TaskPriorityCombobox.tsx'
import { TaskPriorityIcon } from '#src/components/task/TaskPriorityIcon.tsx'
import { TaskStateCombobox } from '#src/components/task/TaskStateCombobox.tsx'
import { TaskStateText } from '#src/components/task/TaskStateText.tsx'
import { TaskLabelComboboxButton } from '#src/components/TaskLabelComboboxButton.tsx'
import {
  createTaskFilter,
  useMarkRelatedInboxItemsAsDone,
} from '#src/hooks/inbox/index.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useLinkOrigin } from '#src/hooks/useLinkOrigin.ts'
import { useParticipants } from '#src/hooks/useParticipants.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { CarePlanCard } from '#src/pages/task/CarePlanCard.tsx'
import { DocumentReviewCard } from '#src/pages/task/DocumentReviewCard.tsx'
import { MedicationRefillCard } from '#src/pages/task/MedicationRefillCard.tsx'
import { SDRCard } from '#src/pages/task/SDRCard.tsx'
import { TaskActivity } from '#src/pages/task/TaskActivity.tsx'

interface TaskPageContentProps {
  task: StandardTask
  hideHeader?: boolean
  stickyHeader?: boolean
  children?: ReactNode
}

export function TaskPageContent({
  task,
  hideHeader = false,
  stickyHeader = true,
  children,
}: TaskPageContentProps): ReactNode {
  const linkOrigin = useLinkOrigin()
  const currentPractitioner = useCurrentPractitioner()
  const medicationRefillUiEnabled = useFeatureFlag(
    featureFlags.medicationRefillTaskingUi
  )
  const inlinePasteDocumentProcessingEnabled = useFeatureFlag(
    featureFlags.inlinePasteDocumentProcessing
  )
  const participants = useParticipants()
  const { data: taskParticipant } = useRead(Participant, task?.participant)
  const markRelatedInboxItemsAsDone = useMarkRelatedInboxItemsAsDone({
    silent: true,
  })

  // Keep the task as provenance while the flagged path also carries its
  // clinical focus. The file handler promotes the participant to subject so
  // the document surfaces on the participant chart.
  const fileHandlerRelated = useMemo(
    () =>
      compact([
        task.reference,
        task.participant,
        inlinePasteDocumentProcessingEnabled ? task.focus : undefined,
      ]),
    [
      inlinePasteDocumentProcessingEnabled,
      task.focus,
      task.participant,
      task.reference,
    ]
  )

  const HeaderWrapper = stickyHeader ? ScrollAreaHeader : 'div'
  const hasChildren = !!children

  return (
    <>
      {!hideHeader && (
        <HeaderWrapper
          className={cn(
            '-mx-4 mt-2',
            !stickyHeader && 'flex flex-wrap gap-2 px-4 pb-4'
          )}
        >
          <ResourceFieldForm
            instance={task}
            valueFn={(t) => t.state}
            patchFn={(t, value) => {
              t.state = value
            }}
            mutationOptions={{
              onSuccess: (instance) => {
                markRelatedInboxItemsAsDone(
                  createTaskFilter([
                    {
                      taskReferenceString: task.reference.reference,
                      state: instance.state,
                    },
                  ])
                )
                if (instance.state === 'done') {
                  toast.success('Task marked done.')
                }
              },
            }}
            render={({ field: { value, onChange, ...rest } }) => (
              <TaskStateCombobox onSelect={onChange} selected={value}>
                <Button variant="outline" className="gap-2" {...rest}>
                  <IconTaskState state={value} />
                  <TaskStateText state={value} />
                </Button>
              </TaskStateCombobox>
            )}
          />
          <ResourceFieldForm
            instance={task}
            valueFn={(t) => t.priority ?? null}
            patchFn={(t, value) => {
              t.priority = value ?? undefined
            }}
            render={({ field: { value, onChange, ...rest } }) => (
              <TaskPriorityCombobox onSelect={onChange} selected={value}>
                <Button variant="outline" className="gap-2" {...rest}>
                  <TaskPriorityIcon
                    priority={value ?? undefined}
                    isTaskCompletedOrCancelled={task.isCompletedOrCancelled}
                  />
                  {value ? StandardTask.priorityText(value) : 'Priority'}
                </Button>
              </TaskPriorityCombobox>
            )}
          />
          <ResourceFieldForm
            instance={task}
            valueFn={(t) => t.ownerPractitioner?.reference ?? null}
            patchFn={(t, value) => {
              t.ownerPractitioner =
                value === null ? undefined : parseReferenceString(value)
              t.requester = currentPractitioner.reference
            }}
            render={({ field }) => <OwnerComboboxButton {...field} />}
          />
          <ResourceFieldForm
            instance={task}
            valueFn={(t) => t.dueOn}
            patchFn={(t, value) => {
              t.dueOn = value ?? undefined
            }}
            render={({ field }) => (
              <DatePicker
                {...field}
                placeholder="Due"
                className="h-10"
                clearText="Remove due date"
              />
            )}
          />
          <div className="flex items-center">
            <ResourceFieldForm
              instance={task}
              valueFn={(t) => t.participant?.reference}
              patchFn={(t, value) => {
                const participant = participants.find(
                  (p) => p.reference.reference === value
                )
                t.participant = participant?.reference
              }}
              render={({ field }) => (
                <ParticipantComboboxButton
                  className={cn(!!taskParticipant && 'rounded-r-none')}
                  {...field}
                />
              )}
            />
            {!!taskParticipant && (
              <Link to={taskParticipant.relativeUrl}>
                <Button
                  variant="outline"
                  size="icon"
                  className="rounded-l-none border-l-0"
                >
                  <IconChevronRight className="size-4" />
                </Button>
              </Link>
            )}
          </div>
          <ResourceFieldForm
            instance={task}
            valueFn={(t) => compact(t.labels.map((l) => l.reference))}
            patchFn={(t, value) => {
              t.labels = compact(value.map((l) => parseReferenceString(l)))
            }}
            render={({ field }) => <TaskLabelComboboxButton {...field} />}
          />
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  void navigator.clipboard.writeText(
                    `${linkOrigin()}/tasks/${task.id}`
                  )
                  toast.info('Task URL copied to clipboard')
                }}
              >
                <IconLink className="size-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent side="bottom" align="center">
              Copy task URL
            </TooltipContent>
          </Tooltip>
        </HeaderWrapper>
      )}
      <ScrollAreaContent className="gap-6 p-0 pt-6">
        <ResourceFieldForm
          instance={task}
          valueFn={(t) => t.title}
          patchFn={(t, value) => {
            t.title = value
          }}
          debounceTyping
          render={({ field: { value, ...fieldRest } }) => (
            <RichTextEditor
              placeholder="Task title"
              variant="h2"
              defaultValue={value}
              contentClassName="p-2 -m-2 w-auto rounded-md"
              related={fileHandlerRelated}
              {...fieldRest}
            />
          )}
        />
        <ResourceFieldForm
          instance={task}
          valueFn={(t) => t.description ?? ''}
          patchFn={(t, value) => {
            t.description = !value ? undefined : value
          }}
          debounceTyping
          render={({ field: { value, ...fieldRest } }) => (
            <RichTextEditor
              placeholder="Add description…"
              variant="p"
              defaultValue={value}
              contentClassName="p-2 -m-2 w-auto rounded-md"
              related={fileHandlerRelated}
              {...fieldRest}
            />
          )}
        />
        <LinkSection task={task} />
        {hasChildren && children}
        {!hasChildren && (
          <>
            {isOfType(task.focus, 'ServiceRequest') && (
              <OrderMention order={task.focus} className="w-fit" />
            )}
            {/* NOTE: technically isInstance only checks if the code is a signature request. We should figure out a better way to narrow the type */}
            {isOfType(task.focus, 'CarePlan') &&
              CarePlanSignatureTask.isInstance(task.toFhirResource()) && (
                <CarePlanCard
                  carePlan={task.focus}
                  task={CarePlanSignatureTask.fromFhirResource(
                    task.toFhirResource()
                  )}
                />
              )}
            {isOfType(task.focus, 'Task') &&
              SDRSignatureTask.isInstance(task.toFhirResource()) && (
                <SDRCard
                  sdr={task.focus}
                  task={SDRSignatureTask.fromFhirResource(
                    task.toFhirResource()
                  )}
                />
              )}
            {DocumentReviewTask.isInstance(task.toFhirResource()) && (
              <DocumentReviewCard
                task={DocumentReviewTask.fromFhirResource(
                  task.toFhirResource()
                )}
              />
            )}
            {QueuedEditsReviewTask.isInstance(task.toFhirResource()) && (
              <QueuedEditsReviewTaskCard
                task={QueuedEditsReviewTask.fromFhirResource(
                  task.toFhirResource()
                )}
              />
            )}
            {medicationRefillUiEnabled &&
              MedicationRefillTask.isInstance(task.toFhirResource()) && (
                <MedicationRefillCard
                  task={MedicationRefillTask.fromFhirResource(
                    task.toFhirResource()
                  )}
                />
              )}
          </>
        )}
        <Separator />
        <Activity resource={task} subject={task.participant}>
          <TaskActivity task={task} />
        </Activity>
      </ScrollAreaContent>
    </>
  )
}

function LinkSection({ task }: { task: StandardTask }): ReactNode {
  const appointmentsQuery = useSearch(
    appointments.ExtendedAppointment,
    {
      'based-on': task.focus,
    },
    { query: { enabled: isOfType(task.focus, 'ServiceRequest') } }
  )
  const relatedBookedAppointments = appointmentsQuery.data ?? []

  if (
    !relatedBookedAppointments.length ||
    !isOfType(task.focus, 'ServiceRequest')
  ) {
    return null
  }

  return (
    relatedBookedAppointments.length > 0 && (
      <div className="flex flex-wrap gap-2">
        {relatedBookedAppointments.map((apt) => (
          <AppointmentBadge key={apt.id} appointment={apt} />
        ))}
      </div>
    )
  )
}
